import {groupBy} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import StorableChip from 'inventory/chips/StorableChip';
import {
  AnalysisInputs,
  AnalysisSourcesOrigin,
  AnalysisSourceRow,
  DatasetType,
  getStepOrDocumentFromSource,
  isLabAutoExecutionDatasetInfo,
  isPlateDesignSource,
  PlateDatasetType,
  PlateDesignSource,
  PlateRecordSource,
  OfflineResultSchemaResponse,
  StepOrDocumentAnalysisSources,
  TableSourceWithSchema,
  TestOrderSource,
  PlateSource,
  isSentinelDocumentOrStep,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureParameterType} from 'procedures/ProcedureDomainTypes';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import DatasetChip from 'ui-library/chips/DatasetChip';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  CreatedAtCellContent,
  DatasetCellContent,
  DataSourcesWithIcons,
  StepsCellContent,
} from './DatasetRendering';
import {
  getIndividualPlateDatasetName,
  getParameterDatasetTypeFromParameterTable,
  getParameterTypeFromDatasetType,
  getPlateDesignSourceIdentifier,
  getPlateRecordSourceIdentifier,
  getPlateSourceIdentifier,
  getPlateSummaryDatasetName,
  ParameterDatasetType,
  STATIC_DATASET_NAMES,
} from './procedureMethodAnalysisUtils';

function getParameterDatasetTypesFromPlate(plate: PlateSource): Array<DatasetType> {
  const datasetTypes = [];
  if (plate.hasDataParameterConfirmations) {
    datasetTypes.push(DatasetType.DATA_PARAMETER);
  }
  if (plate.hasMaterialInputConfirmations) {
    datasetTypes.push(DatasetType.MATERIAL_INPUT);
  }
  return datasetTypes;
}

enum ParameterDatasetSourceType {
  PLATE = 'PLATE',
  TABLE = 'TABLE',
}

function getIndividualPlateRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const plateSources = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.plates.map((plate) => ({
      plateId: plate.plateId,
      plateName: plate.plateName,
      creationTime: null,
      stepOrDocument: getStepOrDocumentFromSource(stepSources),
    }))
  );
  // The same plate could be used in multiple plate maps or lab auto runs, so group by plate ID and get list of all documents where each plate is used
  // StepCellsContent component takes care of de-duping documents
  const plateSourcesByPlateId = groupBy(plateSources, ({plateId}) => plateId);
  const dataPerRow = Object.entries(plateSourcesByPlateId).flatMap(([plateId, plateSources]) => [
    {
      plateId,
      plateSourceId: getPlateSourceIdentifier(plateId, PlateDatasetType.POSITIONS),
      datasetName: getIndividualPlateDatasetName(plateSources[0].plateName, PlateDatasetType.POSITIONS),
      creationTime: null,
      stepsOrDocuments: plateSources.map(({stepOrDocument}) => stepOrDocument),
      datasetType: DatasetType.INDIVIDUAL_PLATE_POSITIONS,
    },
    {
      plateId,
      plateSourceId: getPlateSourceIdentifier(plateId, PlateDatasetType.CONTENTS),
      datasetName: getIndividualPlateDatasetName(plateSources[0].plateName, PlateDatasetType.CONTENTS),
      creationTime: null,
      stepsOrDocuments: plateSources.map(({stepOrDocument}) => stepOrDocument),
      datasetType: DatasetType.INDIVIDUAL_PLATE_CONTENTS,
    },
  ]);
  return dataPerRow.map((rowData) => ({
    id: rowData.plateSourceId,
    data: {
      dataset: <DatasetCellContent datasetName={rowData.datasetName} datasetId={rowData.plateSourceId} />,
      dataSources: <StorableChip id={rowData.plateId} size={ChipSize.MINI} isLink={false} />,
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      steps: <StepsCellContent stepsOrDocuments={rowData.stepsOrDocuments} />,
    },
    extra: {datasetType: rowData.datasetType},
  }));
}

function getPlateSummaryRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const plateSourcesWithDocumentInfo = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.plates.map((plateSource) => ({
      ...plateSource,
      stepOrDocument: getStepOrDocumentFromSource(stepSources),
    }))
  );
  const plateIdsByPlateSchemaId = groupBy(
    plateSourcesWithDocumentInfo,
    (plateSource) => plateSource.plateSchemaId
  );
  const plateSummaryDataPerRow = Object.entries(plateIdsByPlateSchemaId).map(
    ([plateSchemaId, plateSources]) => ({
      schemaId: plateSchemaId,
      // All have the same schema, so can pick schema name from any of them
      schemaName: plateSources[0].plateSchemaName,
      plateSources,
      creationTime: null,
    })
  );
  return plateSummaryDataPerRow.map((rowData) => ({
    id: rowData.schemaId,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={getPlateSummaryDatasetName(rowData.schemaName)}
          datasetId={rowData.schemaId}
        />
      ),
      dataSources: (
        <ChipList
          chipSize={ChipSize.MINI}
          isVertical={false}
          // De-duplicate plates
          itemIds={[...new Set(rowData.plateSources.map((plateSource) => plateSource.plateId))]}
          maxChips={2}
          showChipsAsLinks={false}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      steps: (
        <StepsCellContent
          stepsOrDocuments={rowData.plateSources.map((plateSource) => plateSource.stepOrDocument)}
        />
      ),
    },
    extra: {datasetType: DatasetType.PLATE_SUMMARY},
  }));
}

export function getPlateRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const individualPlateRows = getIndividualPlateRows(availableSourcesPerStep);
  const plateSummaryDatasetRows: Array<AnalysisSourceRow> = [];
  if (individualPlateRows.length > 0) {
    plateSummaryDatasetRows.push(...getPlateSummaryRows(availableSourcesPerStep));
  }
  return plateSummaryDatasetRows.concat(individualPlateRows);
}

export function getPlateRecordAndDesignRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const plateSourceDataPerRow = availableSourcesPerStep.flatMap((stepSources) =>
    [...stepSources.analysisInputs.plateRecords, ...stepSources.analysisInputs.plateDesigns].flatMap(
      (plateMap: PlateRecordSource | PlateDesignSource) => [
        {
          plateMapSourceId: isPlateDesignSource(plateMap)
            ? getPlateDesignSourceIdentifier(plateMap, PlateDatasetType.POSITIONS)
            : getPlateRecordSourceIdentifier(plateMap, PlateDatasetType.POSITIONS),
          plateMapName: plateMap.name,
          datasetName: getIndividualPlateDatasetName(plateMap.name, PlateDatasetType.POSITIONS),
          creationTime: null,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
          datasetType: isPlateDesignSource(plateMap)
            ? DatasetType.PLATE_DESIGN_POSITIONS
            : DatasetType.PLATE_RECORD_POSITIONS,
        },
        {
          plateMapSourceId: isPlateDesignSource(plateMap)
            ? getPlateDesignSourceIdentifier(plateMap, PlateDatasetType.CONTENTS)
            : getPlateRecordSourceIdentifier(plateMap, PlateDatasetType.CONTENTS),
          plateMapName: plateMap.name,
          datasetName: getIndividualPlateDatasetName(plateMap.name, PlateDatasetType.CONTENTS),
          creationTime: null,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
          datasetType: isPlateDesignSource(plateMap)
            ? DatasetType.PLATE_DESIGN_CONTENTS
            : DatasetType.PLATE_RECORD_CONTENTS,
        },
      ]
    )
  );

  return plateSourceDataPerRow.map((rowData) => ({
    id: rowData.plateMapSourceId,
    data: {
      dataset: <DatasetCellContent datasetName={rowData.datasetName} datasetId={rowData.plateMapSourceId} />,
      dataSources: (
        <DataSourcesWithIcons
          dataSources={[{name: rowData.plateMapName, stepName: rowData.stepOrDocument.name}]}
          iconName="plate"
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      steps: <StepsCellContent stepsOrDocuments={[rowData.stepOrDocument]} />,
    },
    extra: {datasetType: rowData.datasetType},
  }));
}

export function getSchemaSourceRows(
  sourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  getTableSources: (analysisInputs: AnalysisInputs) => Array<TableSourceWithSchema>,
  datasetType: DatasetType,
  includeDataSourcesWithoutData: boolean
): Array<AnalysisSourceRow> {
  const tableSourcesWithDocumentInfo = sourcesPerStep.flatMap((stepSources) =>
    getTableSources(stepSources.analysisInputs).map((table) => ({
      schemaId: table.schemaId,
      schemaName: table.schemaName,
      tableName: table.tableName,
      hasCreationContext: table.hasCreationContext,
      stepOrDocument: getStepOrDocumentFromSource(stepSources),
    }))
  );
  const tableSourcesBySchemaId = groupBy(tableSourcesWithDocumentInfo, ({schemaId}) => schemaId);
  const dataPerRow = Object.entries(tableSourcesBySchemaId)
    .map(([schemaId, tableSources]) => ({
      schemaId,
      schemaName: tableSources[0].schemaName,
      tableSources: tableSources.filter(
        (tableSource) => includeDataSourcesWithoutData || tableSource.hasCreationContext
      ),
      creationTime: null,
    }))
    .filter((rowData) => rowData.tableSources.length > 0);
  return dataPerRow.map((rowData) => ({
    id: rowData.schemaId,
    data: {
      dataset: <DatasetCellContent datasetName={rowData.schemaName} datasetId={rowData.schemaId} />,
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      dataSources: (
        <DataSourcesWithIcons
          dataSources={rowData.tableSources.map((tableSource) => ({
            name: tableSource.tableName,
            stepName: tableSource.stepOrDocument.name,
          }))}
          iconName="table"
        />
      ),
      steps: (
        <StepsCellContent stepsOrDocuments={rowData.tableSources.map(({stepOrDocument}) => stepOrDocument)} />
      ),
    },
    extra: {datasetType},
  }));
}

const OUTSIDE_OF_WORKSHEET_LABEL = 'Outside of worksheet';

export function getOfflineOnlyResultSchemaRows(
  offlineResultSchemas: Array<OfflineResultSchemaResponse>,
  inWorksheetSchemaIds: Set<string>
): Array<AnalysisSourceRow> {
  return offlineResultSchemas
    .filter((schema) => !inWorksheetSchemaIds.has(schema.schemaId))
    .map((schema) => ({
      id: schema.schemaId,
      data: {
        dataset: <DatasetCellContent datasetName={schema.schemaName} datasetId={schema.schemaId} />,
        dataSources: <span>{OUTSIDE_OF_WORKSHEET_LABEL}</span>,
        creationTime: <CreatedAtCellContent createdAt={null} />,
        steps: <span>N/A</span>,
      },
      extra: {datasetType: DatasetType.RESULT_SCHEMA},
    }));
}

export function getConditionsDatasetRow(): AnalysisSourceRow {
  return {
    id: DatasetType.CONDITIONS,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={STATIC_DATASET_NAMES[DatasetType.CONDITIONS]!}
          datasetId={DatasetType.CONDITIONS}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      // This dataset only contains condition-replicate information and method start and end times, so it has
      // no data sources within the worksheet
      dataSources: <span>N/A</span>,
      steps: <span>N/A</span>,
    },
    extra: {datasetType: DatasetType.CONDITIONS},
  };
}

/** Renders the test orders dataset row for the analysis source table.
 *
 * Dataset:
 * Test orders when the origin is configuration we can expect for there to be at most one test order
 * across all analysis sources since they are not associated with any specific step. When the origin is execution,
 * there will be a test order response for each document that has a test order. Regardless of how many test orders
 * responses there are, we group them all into one test order dataset row.
 *
 * Data sources:
 * If the analysis sources are from configuration, the data sources will be "Tests & Dilutions".
 * If the analysis sources are from execution, the data sources will be the plates that the test orders were confirmed in.
 * When there are no plates, the data sources will be "N/A".
 *
 * Steps:
 * If the analysis sources are from configuration, the steps will be "N/A".
 * If the analysis sources are from execution, the steps will be the steps that the test orders were confirmed in.
 *
 * @param analysisSourcesOrigin - The origin of the analysis sources (configuration or execution)
 * @param availableSourcesPerStep - The available sources per step
 * @param navigateToTests - The function to navigate to the tests page when the analysis sources are from configuration and
 * the user clicks on the tests & dilutions link
 * @returns The test order dataset row or null if there are no test orders
 */
export function getTestOrderDatasetRow(
  analysisSourcesOrigin: AnalysisSourcesOrigin,
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  navigateToTests?: () => void
): AnalysisSourceRow | null {
  const testOrders = availableSourcesPerStep
    .map((stepSources) => stepSources.analysisInputs.testOrders)
    .filter((testOrder): testOrder is TestOrderSource => testOrder != null);

  if (testOrders.length === 0) {
    return null;
  }

  assert(
    analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION || testOrders.length === 1,
    "Expected at most 1 test order to be present across all analysis sources, but found multiple. This isn't currently supported.",
    {testOrders}
  );

  const datasetType = DatasetType.TEST_ORDERS;
  const plateIds = testOrders.flatMap((testOrder) => testOrder.plateIds);
  let dataSourcesElement: JSX.Element = <span>N/A</span>;
  let steps: JSX.Element = <span>N/A</span>;

  if (analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION) {
    dataSourcesElement = navigateToTests ? (
      <a onClick={navigateToTests}>Tests & Dilutions</a>
    ) : (
      <span>Tests & Dilutions</span>
    );
  }

  if (analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION && plateIds.length > 0) {
    const stepsOrDocument = availableSourcesPerStep
      .filter((stepSources) => stepSources.analysisInputs.testOrders != null)
      // Test orders without a plate association will be in the sentinel document.
      // We don't want to display the sentinel document as a real document, so we need to filter it out.
      .filter((stepSources) => !isSentinelDocumentOrStep(stepSources))
      .map((stepSource) => getStepOrDocumentFromSource(stepSource));
    dataSourcesElement = (
      <ChipList
        chipSize={ChipSize.MINI}
        isVertical={false}
        itemIds={plateIds}
        maxChips={2}
        showChipsAsLinks={false}
      />
    );
    steps = <StepsCellContent stepsOrDocuments={stepsOrDocument} />;
  }

  return {
    id: DatasetType.TEST_ORDERS,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={STATIC_DATASET_NAMES[datasetType]!}
          datasetId={DatasetType.TEST_ORDERS}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      dataSources: dataSourcesElement,
      steps,
    },
    extra: {datasetType},
  };
}

/** Renders the previous test orders dataset row for the analysis source table.
 *
 * There is only one previous test order dataset that groups all previous test orders. This means that we can
 * only expect one previous test order response to be present across all analysis sources. If the analysis
 * sources are from configuration, the data sources will be "Informed by execution". If the analysis sources
 * are from execution, the data sources will be the studies the previous test orders are associated to.
 * If there are no studies, the data sources will be "N/A".
 */
export function getPreviousTestOrderDatasetRow(
  analysisSourcesOrigin: AnalysisSourcesOrigin,
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): AnalysisSourceRow | null {
  const previousTestOrders = availableSourcesPerStep
    .map((stepSources) => stepSources.analysisInputs.previousTestOrders)
    .filter((previousTestOrders) => previousTestOrders != null);

  if (previousTestOrders.length === 0) {
    return null;
  }

  assert(
    previousTestOrders.length === 1,
    "Expected at most 1 previous test order to be present across all analysis sources, but found multiple. This isn't currently supported.",
    {previousTestOrders}
  );

  const datasetType = DatasetType.PREVIOUS_TEST_ORDERS;
  const studies = previousTestOrders[0].studies;

  let dataSourcesElement: JSX.Element =
    analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION ? (
      <span>Informed by execution</span>
    ) : (
      <span>N/A</span>
    );

  if (analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION && studies.length > 0) {
    const studyDataSources = studies.map((studyInfo) => ({
      name: studyInfo.name,
      url: studyInfo.editUrl,
    }));
    dataSourcesElement =
      studyDataSources.length > 0 ? (
        <DataSourcesWithIcons
          dataSources={studyDataSources}
          iconName="study"
          onlyEnablePopoverOnOverflow={true}
        />
      ) : (
        dataSourcesElement
      );
  }

  return {
    id: DatasetType.PREVIOUS_TEST_ORDERS,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={STATIC_DATASET_NAMES[datasetType]!}
          datasetId={DatasetType.PREVIOUS_TEST_ORDERS}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      dataSources: dataSourcesElement,
      steps: <span>N/A</span>,
    },
    extra: {datasetType},
  };
}

export function getTestOrdersDatasetRows(
  analysisSourcesOrigin: AnalysisSourcesOrigin,
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  navigateToTests?: () => void
): Array<AnalysisSourceRow> {
  const testOrderRow: AnalysisSourceRow | null = getTestOrderDatasetRow(
    analysisSourcesOrigin,
    availableSourcesPerStep,
    navigateToTests
  );
  const previousTestOrderRow: AnalysisSourceRow | null = getPreviousTestOrderDatasetRow(
    analysisSourcesOrigin,
    availableSourcesPerStep
  );

  return [testOrderRow, previousTestOrderRow].filter((row): row is AnalysisSourceRow => row != null);
}

export function getParameterDatasetRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  availableParameterTypes: Array<ProcedureParameterType>,
  includeDataSourcesWithoutData: boolean
): Array<AnalysisSourceRow> {
  const baseParameterTableSources = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.parameterTables.map((parameterTable) => ({
      parameterDatasetType: getParameterDatasetTypeFromParameterTable(parameterTable.mode),
      tableName: parameterTable.tableName,
      hasCreationContext: parameterTable.hasCreationContext,
      stepOrDocument: getStepOrDocumentFromSource(stepSources),
    }))
  );
  // If necessary, filter out tables that would produce empty datasets
  const filteredParameterTableSources = baseParameterTableSources.filter(
    (parameterTableSource) =>
      includeDataSourcesWithoutData ||
      // Only the material output confirmation tables need to be filtered because all other parameter dataset types have planned values, which are always present
      parameterTableSource.parameterDatasetType !== DatasetType.MATERIAL_OUTPUT ||
      parameterTableSource.hasCreationContext
  );
  // Standardize to type consistent with plate source type
  const standardizedParameterTableSources = filteredParameterTableSources.map((parameterTableSource) => ({
    parameterDatasetType: parameterTableSource.parameterDatasetType,
    sourceInfo: {
      sourceIdentifier: parameterTableSource.tableName,
      sourceType: ParameterDatasetSourceType.TABLE,
      stepOrDocument: parameterTableSource.stepOrDocument,
    },
  }));

  const plateSources = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.plates.flatMap((plate) =>
      getParameterDatasetTypesFromPlate(plate).map((parameterDatasetType) => ({
        parameterDatasetType,
        sourceInfo: {
          sourceIdentifier: plate.plateId,
          sourceType: ParameterDatasetSourceType.PLATE,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
        },
      }))
    )
  );
  // Include these so table rows for parameter datasets that have parameter values but don't have any data sources (e.g. if table was deleted) are included
  const parameterDatasetTypes: Array<ParameterDatasetType> = [
    DatasetType.DATA_PARAMETER,
    DatasetType.MATERIAL_INPUT,
    DatasetType.MATERIAL_OUTPUT,
    DatasetType.EQUIPMENT,
  ];
  const rawParameterSources = parameterDatasetTypes
    .filter((parameterDatasetType) => {
      const parameterType = getParameterTypeFromDatasetType(parameterDatasetType);
      return availableParameterTypes.includes(parameterType);
    })
    .map((parameterDatasetType) => ({
      parameterDatasetType,
      sourceInfo: null,
    }));
  const sourcesByDatasetType = groupBy(
    [...standardizedParameterTableSources, ...plateSources, ...rawParameterSources],
    ({parameterDatasetType}) => parameterDatasetType
  );

  const dataPerRow = Object.entries(sourcesByDatasetType).map(([parameterDatasetType, sources]) => ({
    datasetType: parameterDatasetType,
    creationTime: null,
    sources,
  }));

  return dataPerRow.map((rowData) => {
    const inDocumentSources = rowData.sources.filter((source) => source.sourceInfo != null);
    const tableSources = inDocumentSources.filter(
      // Already filtered out sources without source info above, but TypeScript doesn't know that
      (source) =>
        source.sourceInfo != null && source.sourceInfo.sourceType === ParameterDatasetSourceType.TABLE
    );
    const plateSources = inDocumentSources.filter(
      // Already filtered out sources without source info above, but TypeScript doesn't know that
      (source) =>
        source.sourceInfo != null && source.sourceInfo.sourceType === ParameterDatasetSourceType.PLATE
    );

    return {
      id: rowData.datasetType,
      data: {
        dataset: (
          <DatasetCellContent
            datasetName={STATIC_DATASET_NAMES[rowData.datasetType as DatasetType]!}
            datasetId={rowData.datasetType}
          />
        ),
        creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
        dataSources: (
          <div>
            {tableSources.length > 0 && (
              <DataSourcesWithIcons
                dataSources={tableSources.map(({sourceInfo}) => ({
                  name: sourceInfo.sourceIdentifier,
                  stepName: sourceInfo.stepOrDocument.name,
                }))}
                iconName="table"
              />
            )}
            {plateSources.length > 0 && (
              <ChipList
                chipSize={ChipSize.MINI}
                isVertical={false}
                itemIds={[...new Set(plateSources.map(({sourceInfo}) => sourceInfo.sourceIdentifier))]}
                maxChips={2}
                showChipsAsLinks={false}
              />
            )}
            {inDocumentSources.length === 0 && 'N/A'}
          </div>
        ),
        steps:
          inDocumentSources.length !== 0 ? (
            <StepsCellContent
              stepsOrDocuments={inDocumentSources.map(({sourceInfo}) => sourceInfo.stepOrDocument)}
            />
          ) : (
            <div>N/A</div>
          ),
        // If both table sources and plate sources are being rendered (as two lines in the table row), increase the row height so it doesn't look squished
        height: tableSources.length && plateSources.length ? TABLE_ROW_BODY_HEIGHT * 2 : undefined,
      },
      extra: {datasetType: rowData.datasetType as DatasetType},
    };
  });
}

export function getLabAutoDatasetRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const dataPerRow = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.laRuns.flatMap((laRun) =>
      laRun.outputs.map((output) => ({
        datasetId: isLabAutoExecutionDatasetInfo(output) ? output.datasetId : undefined,
        schemaName: laRun.schemaName,
        outputFileConfigName: output.outputFileConfigName,
        outputFileConfigId: output.outputFileConfigId,
        creationTime: isLabAutoExecutionDatasetInfo(output) ? output.createdAt : null,
        stepOrDocument: getStepOrDocumentFromSource(stepSources),
      }))
    )
  );
  return dataPerRow.map((rowData) => ({
    id: rowData.datasetId ?? rowData.outputFileConfigId,
    data: {
      dataset:
        rowData.datasetId != null ? (
          <DatasetChip id={rowData.datasetId} size={ChipSize.MINI} isLink={false} />
        ) : (
          <DatasetCellContent
            datasetName={rowData.outputFileConfigName}
            datasetId={rowData.outputFileConfigId}
          />
        ),
      dataSources: (
        <DataSourcesWithIcons
          dataSources={[
            {
              name: `${rowData.schemaName}${
                rowData.outputFileConfigName != null ? ` (${rowData.outputFileConfigName})` : ''
              }`,
              stepName: rowData.stepOrDocument.name,
            },
          ]}
          iconName="assay_run"
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      steps: <StepsCellContent stepsOrDocuments={[rowData.stepOrDocument]} />,
    },
    extra: {datasetType: DatasetType.LAB_AUTO},
  }));
}
